import React, { useState, useCallback } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  FileSignature,
  User,
  Shield,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Phone,
  BadgeCheck,
  Briefcase,
  Users,
  Copy,
  Landmark,
  Pencil,
  Plus,
} from 'lucide-react';
import { getThemeColor } from '../config/appConfig';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import apiService from '../utils/apiService';
import {
  formatProfileDate,
  formatProfilePhone,
  parseUserProfile,
  profileToEditForm,
} from '../utils/userProfile';
import { useFetchOnceByKey } from '../hooks/useMountOnce';
import PartnerAgreements from './agreements/PartnerAgreements';
import { AGREEMENTS_PATH } from '../utils/partnerAgreements';

import WalletMethodIcon from '../components/withdrawal/WalletMethodIcon';
import { resolveWalletIconUrl } from '../utils/walletIcons';
import {
  extractPartnerPaymentDetailsList,
  mapPartnerPaymentDetail,
  maskAccountNumber,
  shortenAddress,
} from '../utils/partnerPaymentDetails';


const THEME = getThemeColor('primary');

const PROFILE_TABS = [
  { to: '/profile', label: 'Profile', icon: User, end: true },
  { to: AGREEMENTS_PATH, label: 'Agreements', icon: FileSignature, end: false },
];

const ProfileTabs = () => (
  <div role="tablist" className="flex flex-wrap items-end gap-x-10 gap-y-2 border-b border-gray-200">
    {PROFILE_TABS.map(({ to, label, icon: Icon, end }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        role="tab"
        className={({ isActive }) =>
          `relative inline-flex items-center gap-2.5 pb-3 pt-1 text-[15px] whitespace-nowrap rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200 ${
            isActive ? 'font-semibold' : 'font-medium text-slate-500 hover:text-slate-800'
          }`
        }
        style={({ isActive }) => (isActive ? { color: THEME } : undefined)}
      >
        {({ isActive }) => (
          <>
            <Icon className="w-5 h-5" strokeWidth={1.75} />
            {label}
            {/* Active indicator sits on the bar's bottom border — no negative margins, so nothing overflows. */}
            <span
              aria-hidden="true"
              className={`absolute left-0 right-0 bottom-0 h-0.5 rounded-full transition-opacity ${
                isActive ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ backgroundColor: THEME }}
            />
          </>
        )}
      </NavLink>
    ))}
  </div>
);

const inputCls =
  'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-800/15';

const SectionLoader = ({ className = 'py-10' }) => (
  <div className={`flex items-center justify-center ${className}`}>
    <NewtonsCradleLoader size="28px" />
  </div>
);

const VerificationBadge = ({ verified, label }) =>
  verified ? (
    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
      Verified
    </span>
  ) : (
    <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
      {label || 'Not Verified'}
    </span>
  );

const StatusBadge = ({ status }) => {
  const value = (status || 'Pending').toString();
  const lower = value.toLowerCase();
  const cls =
    lower === 'approved'
      ? 'text-emerald-700 bg-emerald-50'
      : lower === 'pending'
        ? 'text-amber-700 bg-amber-50'
        : 'text-gray-700 bg-gray-100';
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{value}</span>
  );
};

const AlertMessage = ({ type, message }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div
      className={`mb-4 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${
        isError ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
      }`}
    >
      {isError ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <Check className="w-4 h-4 shrink-0" />}
      <span>{message}</span>
    </div>
  );
};

const ProfileStatCard = ({ title, value, icon: Icon, editable, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
    <div className="flex items-center gap-3.5 sm:gap-4">
      <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
        <Icon className="w-6 h-6" style={{ color: THEME }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 leading-tight">{title}</p>
        {editable ? (
          children
        ) : (
          <p className="text-xl sm:text-[22px] font-bold text-gray-900 leading-tight mt-1 tracking-tight">
            {value || '—'}
          </p>
        )}
      </div>
    </div>
  </div>
);

const ProfileField = ({ label, value, editable, children, badge }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <p className="text-xs text-gray-500">{label}</p>
      {badge}
    </div>
    {editable ? children : <p className="text-sm font-semibold text-gray-900 break-words">{value || '—'}</p>}
  </div>
);

const PasswordField = ({ label, value, onChange, visible, onToggle, placeholder }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1.5">{label}</label>
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        className={`${inputCls} pr-10`}
        placeholder={placeholder}
        required
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

const WITHDRAWAL_DETAILS_PATH = '/settings/withdrawal-details';

/** Read-only view of the partner's saved bank / payout accounts. Add and edit happen on the settings page. */
const BankDetailsSection = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revealed, setRevealed] = useState(() => new Set());

  const toggleReveal = (key) =>
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const fetchAccounts = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.getPartnerPaymentDetails();
      if (cancelled()) return;
      if (response?.success) {
        setAccounts(extractPartnerPaymentDetailsList(response).map(mapPartnerPaymentDetail));
      } else {
        setAccounts([]);
        setError(response?.message || response?.error || 'Unable to load bank details.');
      }
    } catch {
      if (!cancelled()) {
        setAccounts([]);
        setError('Unable to load bank details. Please try again.');
      }
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('profile-bank-details', fetchAccounts, true);

  const goAdd = () => navigate(WITHDRAWAL_DETAILS_PATH, { state: { add: true } });
  const goEdit = (id) => navigate(WITHDRAWAL_DETAILS_PATH, { state: { editId: id } });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
            <Landmark className="w-5 h-5" style={{ color: THEME }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Bank &amp; Withdrawal Details</h3>
            <p className="text-sm text-gray-500">The accounts your payouts are sent to</p>
          </div>
        </div>
        <button
          type="button"
          onClick={goAdd}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-semibold hover:bg-red-50 transition"
          style={{ borderColor: THEME, color: THEME }}
        >
          <Plus className="w-4 h-4" />
          {accounts.length > 0 ? 'Add / Manage' : 'Add Account'}
        </button>
      </div>

      {loading ? (
        <SectionLoader className="py-8" />
      ) : error ? (
        <div className="rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      ) : accounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 py-8 px-4 text-center">
          <p className="text-sm font-semibold text-gray-900">No bank or payout account added yet</p>
          <p className="text-xs text-gray-500 mt-1">Add an account to receive your withdrawals.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {accounts.map((account) => {
            const isBank = account.methodKey === 'bank';
            const iconUrl =
              resolveWalletIconUrl({
                title: account.subPaymentMethodTitle || account.walletTitle,
                value: account.raw?.accountCode,
              }) || resolveWalletIconUrl({ title: account.walletTitle });
            const rows = isBank
              ? [
                  { label: 'Bank Name', value: account.bankName },
                  { label: 'Account Title', value: account.accountTitle },
                  { label: 'Account Number', key: 'acc', secret: String(account.accountNumber || '').trim() },
                  { label: 'IBAN #', key: 'iban', secret: String(account.iban || account.swiftCode || '').trim() },
                ]
              : [
                  { label: 'Account Title', value: account.accountTitle },
                  { label: 'Payment Network', value: account.subPaymentMethodTitle || account.walletTitle },
                  { label: 'Wallet Address', value: shortenAddress(account.cryptoAddress) },
                ];
            return (
              <div key={account.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                      <WalletMethodIcon
                        iconUrl={iconUrl}
                        methodKey={account.methodKey}
                        imgClassName="w-6 h-6 object-contain"
                        iconClassName="w-5 h-5 text-red-800"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{account.walletTitle}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{account.accountType || (isBank ? 'Bank' : 'Crypto')}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => goEdit(account.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition shrink-0"
                    aria-label={`Edit ${account.walletTitle}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl bg-gray-50 p-3.5">
                  {rows.map((row) => {
                    const isSecret = row.secret !== undefined;
                    const revealKey = `${account.id}:${row.key}`;
                    const shown = isSecret && revealed.has(revealKey);
                    const display = isSecret
                      ? row.secret
                        ? shown ? row.secret : maskAccountNumber(row.secret)
                        : '—'
                      : row.value || '—';
                    return (
                      <div key={row.label} className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500">{row.label}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-sm font-medium text-gray-900 break-all">{display}</p>
                          {isSecret && row.secret ? (
                            <button
                              type="button"
                              onClick={() => toggleReveal(revealKey)}
                              className="shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 transition"
                              aria-label={shown ? `Hide ${row.label}` : `Show ${row.label}`}
                              aria-pressed={shown}
                              title={shown ? 'Hide' : 'Show'}
                            >
                              {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const location = useLocation();
  const { mappingId } = useParams();
  const isSecurityPage = location.pathname === '/settings/security';
  const isAgreementsPage = location.pathname.startsWith(AGREEMENTS_PATH);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileUpdateMessage, setProfileUpdateMessage] = useState('');
  const [profileUpdateError, setProfileUpdateError] = useState('');
  const [editFormData, setEditFormData] = useState({
    state: '',
    city: '',
    zipCode: '',
    address: '',
    lastCompanyName: '',
    totalClients: '',
    totalTradingExp: '',
    dateOfBirth: '',
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [affiliateCodeCopied, setAffiliateCodeCopied] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordUpdateMessage, setPasswordUpdateMessage] = useState('');
  const [passwordUpdateError, setPasswordUpdateError] = useState('');

  const fetchUserProfile = useCallback(async ({ cancelled }) => {
    try {
      setProfileLoading(true);
      const response = await apiService.getUserProfile();
      if (cancelled()) return;
      if (response.success && response.data) {
        const profile = parseUserProfile(response.data);
        setProfileData(profile);
        setEditFormData(profileToEditForm(profile));
      }
    } catch {
      /* keep defaults */
    } finally {
      if (!cancelled()) setProfileLoading(false);
    }
  }, []);

  useFetchOnceByKey('user-profile', fetchUserProfile, !isAgreementsPage);

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileUpdateMessage('');
    setProfileUpdateError('');

    const requiredFields = [
      { field: 'state', label: 'State' },
      { field: 'city', label: 'City' },
      { field: 'zipCode', label: 'Zip Code' },
      { field: 'address', label: 'Address' },
      { field: 'lastCompanyName', label: 'Last Company Name' },
      { field: 'totalClients', label: 'Total Clients' },
      { field: 'totalTradingExp', label: 'Trading Experience' },
    ];

    const emptyFields = requiredFields.filter(
      (item) => !editFormData[item.field] || editFormData[item.field].trim() === ''
    );

    if (emptyFields.length > 0) {
      setProfileUpdateError(`Please fill in all required fields: ${emptyFields.map((f) => f.label).join(', ')}`);
      return;
    }

    setIsUpdatingProfile(true);

    try {
      const response = await apiService.updateUserProfile(editFormData);
      if (response.success) {
        setProfileUpdateMessage('Profile updated successfully!');

        try {
          const profileResponse = await apiService.getUserProfile();
          if (profileResponse.success && profileResponse.data) {
            const profile = parseUserProfile(profileResponse.data);
            setProfileData(profile);
            setEditFormData(profileToEditForm(profile));
          }
        } catch {
          setProfileData((prev) => (prev ? { ...prev, ...editFormData } : prev));
        }

        setTimeout(() => setProfileUpdateMessage(''), 3000);
      } else {
        setProfileUpdateError(response.message || 'Failed to update profile');
      }
    } catch (error) {
      setProfileUpdateError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordUpdateMessage('');
    setPasswordUpdateError('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordUpdateError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordUpdateError('New password must be at least 6 characters long');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const response = await apiService.updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );

      if (response.success) {
        setPasswordUpdateMessage('Password updated successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
        setTimeout(() => setPasswordUpdateMessage(''), 3000);
      }
    } catch (error) {
      setPasswordUpdateError(error.message || 'Failed to update password. Please try again.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleCopyPartnerUrl = async () => {
    if (!profileData?.partnerUrl) return;
    try {
      await navigator.clipboard.writeText(profileData.partnerUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      setCopySuccess(false);
    }
  };

  const handleCopyAffiliateCode = async () => {
    const code = profileData?.affiliateCode?.toString().trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setAffiliateCodeCopied(true);
      setTimeout(() => setAffiliateCodeCopied(false), 2000);
    } catch {
      setAffiliateCodeCopied(false);
    }
  };

  const isEditable = profileData && !profileData.isAccountVerified;
  const pageTitle = isSecurityPage ? 'Security' : 'Profile';
  const pageSubtitle = isSecurityPage
    ? 'Manage your password and account security'
    : isAgreementsPage
      ? 'Review, download and sign your Partner Agreements'
      : 'Manage your profile information';

  const renderProfileContent = () => {
    if (profileLoading) {
      return (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <SectionLoader />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
                <SectionLoader className="py-6" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (!profileData) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-600" />
          <p className="text-sm font-semibold text-gray-900">Failed to load profile data</p>
          <p className="text-sm text-gray-500 mt-1">Please try refreshing the page</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-gray-900">{profileData.fullName}</h2>
            <StatusBadge status={profileData.status} />
          </div>
          <div className="space-y-1.5 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-2">
              <Mail className="w-4 h-4 shrink-0" style={{ color: THEME }} />
              <span>{profileData.email || '—'}</span>
              <VerificationBadge verified={profileData.isEmailVerified} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Phone className="w-4 h-4 shrink-0" style={{ color: THEME }} />
              <span>{formatProfilePhone(profileData.countryCode, profileData.mobileNumber)}</span>
              <VerificationBadge verified={profileData.isNumberVerified} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ProfileStatCard title="Total Clients" value={profileData.totalClients} icon={Users} editable={isEditable}>
            <input
              type="text"
              value={editFormData.totalClients}
              onChange={(e) => handleEditFormChange('totalClients', e.target.value)}
              className={`${inputCls} mt-1 font-bold`}
              placeholder="Enter total clients"
              required
            />
          </ProfileStatCard>
          <ProfileStatCard
            title="Trading Experience"
            value={profileData.totalTradingExp}
            icon={Briefcase}
            editable={isEditable}
          >
            <input
              type="text"
              value={editFormData.totalTradingExp}
              onChange={(e) => handleEditFormChange('totalTradingExp', e.target.value)}
              className={`${inputCls} mt-1 font-bold`}
              placeholder="Enter trading experience"
              required
            />
          </ProfileStatCard>
          <ProfileStatCard title="Account Status" value={profileData.status || 'Pending'} icon={BadgeCheck} />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Partner Information</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <p className="text-xs text-gray-500">Partner Code</p>
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-sm font-semibold text-gray-900 break-all">
                  {profileData.affiliateCode || '—'}
                </p>
                <button
                  type="button"
                  onClick={handleCopyAffiliateCode}
                  disabled={!profileData.affiliateCode}
                  className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition disabled:opacity-40 disabled:pointer-events-none"
                  aria-label="Copy partner code"
                  title={affiliateCodeCopied ? 'Copied' : 'Copy partner code'}
                >
                  {affiliateCodeCopied ? (
                    <Check className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                  ) : (
                    <Copy className="w-4 h-4" strokeWidth={1.75} />
                  )}
                </button>
              </div>
              {affiliateCodeCopied ? (
                <p className="text-xs text-emerald-600 font-medium">Copied to clipboard!</p>
              ) : null}
            </div>
            <ProfileField label="Member Since" value={formatProfileDate(profileData.createdDateTime)} />
            <div className="lg:col-span-2">
              <p className="text-xs text-gray-500 mb-1.5">Partner URL</p>
              <div
                className="flex items-center rounded-xl border bg-white overflow-hidden"
                style={{ borderColor: '#fecaca' }}
              >
                <span className="flex-1 min-w-0 text-sm font-medium truncate px-4 py-3" style={{ color: THEME }}>
                  {profileData.partnerUrl || '—'}
                </span>
                <button
                  type="button"
                  onClick={handleCopyPartnerUrl}
                  disabled={!profileData.partnerUrl}
                  className="shrink-0 inline-flex items-center gap-2 mx-2 px-3 py-2 rounded-lg border-2 text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
                  style={{ borderColor: THEME, color: THEME }}
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
              {copySuccess && (
                <p className="text-xs text-emerald-600 font-medium mt-2">Copied to clipboard!</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ProfileField label="Full Name" value={profileData.fullName} />
            <ProfileField
              label="Date of Birth"
              value={formatProfileDate(profileData.dateOfBirth)}
              editable={isEditable}
            >
              <input
                type="date"
                value={editFormData.dateOfBirth}
                onChange={(e) => handleEditFormChange('dateOfBirth', e.target.value)}
                className={inputCls}
                max={new Date().toISOString().slice(0, 10)}
              />
            </ProfileField>
            <ProfileField label="Last Company" value={profileData.lastCompanyName} editable={isEditable}>
              <input
                type="text"
                value={editFormData.lastCompanyName}
                onChange={(e) => handleEditFormChange('lastCompanyName', e.target.value)}
                className={inputCls}
                placeholder="Enter company name"
                required
              />
            </ProfileField>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Contact & Address</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <ProfileField
              label="Email Address"
              value={profileData.email}
              badge={<VerificationBadge verified={profileData.isEmailVerified} />}
            />
            <ProfileField
              label="Mobile Number"
              value={formatProfilePhone(profileData.countryCode, profileData.mobileNumber)}
              badge={<VerificationBadge verified={profileData.isNumberVerified} />}
            />
            <ProfileField label="Country" value={profileData.countryName} />
            <ProfileField label="State" value={profileData.state} editable={isEditable}>
              <input
                type="text"
                value={editFormData.state}
                onChange={(e) => handleEditFormChange('state', e.target.value)}
                className={inputCls}
                placeholder="Enter state"
                required
              />
            </ProfileField>
            <ProfileField label="City" value={profileData.city} editable={isEditable}>
              <input
                type="text"
                value={editFormData.city}
                onChange={(e) => handleEditFormChange('city', e.target.value)}
                className={inputCls}
                placeholder="Enter city"
                required
              />
            </ProfileField>
            <ProfileField label="Zip Code" value={profileData.zipCode} editable={isEditable}>
              <input
                type="text"
                value={editFormData.zipCode}
                onChange={(e) => handleEditFormChange('zipCode', e.target.value)}
                className={inputCls}
                placeholder="Enter zip code"
                required
              />
            </ProfileField>
            <div className="sm:col-span-2 lg:col-span-3">
              <ProfileField label="Address" value={profileData.address} editable={isEditable}>
                <input
                  type="text"
                  value={editFormData.address}
                  onChange={(e) => handleEditFormChange('address', e.target.value)}
                  className={inputCls}
                  placeholder="Enter address"
                  required
                />
              </ProfileField>
            </div>
          </div>
        </div>

        {isEditable && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
            <AlertMessage type="success" message={profileUpdateMessage} />
            <AlertMessage type="error" message={profileUpdateError} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">Save Your Changes</h3>
                <p className="text-sm text-gray-500 mt-0.5">Complete your profile to verify your account</p>
              </div>
              <button
                type="button"
                onClick={handleProfileUpdate}
                disabled={isUpdatingProfile}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition disabled:opacity-50"
                style={{ backgroundColor: THEME }}
              >
                {isUpdatingProfile ? <NewtonsCradleLoader size="18px" color="#fff" /> : <Check className="w-4 h-4" />}
                {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </div>
        )}

        <BankDetailsSection />
      </div>
    );
  };

  const renderSecurityContent = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <Shield className="w-5 h-5" style={{ color: THEME }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Security Status</h3>
            <p className="text-sm text-gray-500">Your account security overview</p>
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" style={{ color: THEME }} />
            <span className="text-sm font-semibold text-gray-900">Password</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Keep your password strong and unique</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">Change Password</h3>
            <p className="text-sm text-gray-500 mt-0.5">Update your account password</p>
          </div>
          {!showPasswordForm && (
            <button
              type="button"
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 rounded-xl border-2 text-sm font-semibold hover:bg-red-50 transition"
              style={{ borderColor: THEME, color: THEME }}
            >
              Change Password
            </button>
          )}
        </div>

        {showPasswordForm && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4 border-t border-gray-100 pt-4">
            <AlertMessage type="success" message={passwordUpdateMessage} />
            <AlertMessage type="error" message={passwordUpdateError} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PasswordField
                label="Current Password"
                value={passwordForm.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                visible={showPasswords.current}
                onToggle={() => togglePasswordVisibility('current')}
                placeholder="Current password"
              />
              <PasswordField
                label="New Password"
                value={passwordForm.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                visible={showPasswords.new}
                onToggle={() => togglePasswordVisibility('new')}
                placeholder="New password"
              />
              <PasswordField
                label="Confirm Password"
                value={passwordForm.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                visible={showPasswords.confirm}
                onToggle={() => togglePasswordVisibility('confirm')}
                placeholder="Confirm password"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={isUpdatingPassword}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME }}
              >
                {isUpdatingPassword ? <NewtonsCradleLoader size="18px" color="#fff" /> : null}
                {isUpdatingPassword ? 'Updating...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordUpdateMessage('');
                  setPasswordUpdateError('');
                }}
                disabled={isUpdatingPassword}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="px-4 sm:px-6 py-5 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
        <p className="text-sm text-gray-500 mt-1">
          <Link to="/" className="hover:text-red-800">
            Dashboard
          </Link>
          <span className="mx-1.5">›</span>
          <span>{pageSubtitle}</span>
        </p>
      </div>

      {!isSecurityPage && <ProfileTabs />}

      {isSecurityPage
        ? renderSecurityContent()
        : isAgreementsPage
          ? <PartnerAgreements mappingId={mappingId} />
          : renderProfileContent()}
    </div>
  );
};

export default Profile;
